/*
  NUDIENT: Produktsidan, köpkolumnen, paket D1, M-084. Plan:
  nudient/PLAN-D1-kopkolumn.md. Laddas av snippets/nudient-tokens.liquid.

  Talen i köpkolumnen följer sidans språk, M-084 tillägget efter del b punkt
  3: på svenska 4,8 och 1 830, på engelska 4.8 och 1,830. Liquid har inget
  filter som formaterar tal per språk, så webbläsarens Intl.NumberFormat gör
  det, med språket i <html lang>, som Horizon sätter till request.locale.
  Inget skrivs per språk i koden.

  Ett element med data-nudient-number="4.8" har talet som Liquid skrev det, i
  sin text. Talet byts mot det formaterade, och resten av texten står kvar.
  data-nudient-decimals anger antalet decimaler, 0 om det saknas. Utan skript
  står talet som Liquid skrev det.

  Tonen på MagSafe-märket i färgpricken, ColorSwatch 205:137, del c och
  planen punkt 15.4: "The theme picks the tone from the swatch colour
  luminance, compute it, do not hardcode it". Prickens färg jämförs med de två
  färger märket kan ha i schemat, color/text/inverse och color/text/primary,
  och märket får den som ger högst kontrast. Inget gränsvärde och inget
  färgnamn avgör. Liquid kan inte läsa schemats färger, som bara finns i
  assets/nudient-tokens.css, så webbläsaren räknar. Utan skript har märket
  den mörka tonen.

  Rutan med färgnamnet, M-084 granskningen av del c punkt 1: visas bara på
  enheter med mus, så att pekskärmar aldrig får den. Skriptet sätter
  data-nudient-hover på <html> när webbläsaren säger att den kan hovra. Det
  görs här och inte med en mediafråga i CSS, eftersom Nudients filer bara
  använder de fyra mediafrågorna för bredd, M-017. Skriptet flyttar också
  rutan i sidled så att den stannar inom färgraden.

  Sömlöst byte av färg och storlek, M-084 tillägget efter godkännandet av
  del c, planen punkt 15.13. Gäller också setväljarens kort, del f, och
  modellradens rader och MagSafe-knapp, del g. Bytet är fortfarande en riktig sidladdning.
  Vid modellbyte behålls inte scrollningen, och kunden hamnar högst upp,
  tillägget till M-084 8 oktober 2026. Raderna förhämtas fortfarande, och
  övergången körs, men utan scrollning: uppgiften som sparas har bara
  adressen, och snippets/nudient-keep-place.liquid slår då på övergången
  utan att scrolla.
  Modellradens rader förhämtas först när systern är uppslagen, M-086: rader
  med data-nudient-pending hoppas över, och assets/nudient-model-picker.js
  skickar händelsen nudient:prefetch när adressen är klar.
  - Förhämtning: när muspekaren kommer över en prick eller storlek, eller ett
    finger sätts på den, ber skriptet webbläsaren hämta sidan i förväg med en
    speculation rule, och annars med rel prefetch. Förhämtningen kör inga
    skript på den hämtade sidan, så ingen sidvisning räknas förrän kunden
    klickar.
  - Vid ett vanligt klick sparas hur långt ner kunden var och var raden står
    på skärmen, och övergången
    mellan sidor slås på för den här sidladdningen, inte för den som valt
    minskad rörelse. Den nya sidan läser uppgiften i
    snippets/nudient-keep-place.liquid. Klick med Cmd, Ctrl, Skift, Alt
    eller mittenknappen öppnar som vanligt, utan något av detta.
  - Kommer kunden tillbaka med bakåtknappen tas stilen för övergången bort,
    så att bakåtknappen fungerar som förut.

  Värden som inte är tokens:
  - 0: inga decimaler som standard.
  - nudient-keep-place, nudient-view-transition och page-navigation: samma
    som i snippets/nudient-keep-place.liquid.
  - 0 i event.button: vänster musknapp.
  - _self: länkar som öppnas i samma flik.
  - (min-width: 990px) och .page-wrapper: Horizons gräns och behållare för
    scrollningen, assets/scroll-container.js. Ingen mediafråga i CSS.
  - (hover: hover): webbläsarens fråga om enheten kan hovra.
  - px: förskjutningen av rutan i bildpunkter.
  - 0.2126, 0.7152, 0.0722, 0.03928, 12.92, 0.055, 1.055, 2.4 och 0.05:
    formeln för relativ luminans och kontrast i WCAG 2.
  - 255: största värdet per färgkanal.
  - 1: canvasens storlek, bara för att läsa en färg som tal.
  - light och dark: tonerna i data-nudient-tone.
*/

const locale = document.documentElement.lang || undefined;

function formatNumbers(root = document) {
  for (const el of root.querySelectorAll('[data-nudient-number]')) {
    const raw = el.dataset.nudientNumber;
    const value = Number(raw);
    /* Attributet tas alltid bort, så att talet blir synligt, också när det
       inte går att formatera, assets/nudient-product.css. */
    if (!raw || !Number.isFinite(value) || !el.textContent.includes(raw)) {
      el.removeAttribute('data-nudient-number');
      continue;
    }

    const decimals = Number(el.dataset.nudientDecimals || 0);
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

    el.textContent = el.textContent.replace(raw, formatted);
    el.removeAttribute('data-nudient-number');
  }
}

const colourReader = document.createElement('canvas').getContext('2d', { willReadFrequently: true });

/* En CSS-färg som tre kanaler 0 till 255 */
function readColour(value) {
  colourReader.clearRect(0, 0, 1, 1);
  colourReader.fillStyle = value;
  colourReader.fillRect(0, 0, 1, 1);
  const [r, g, b] = colourReader.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
}

function luminance([r, g, b]) {
  const [lr, lg, lb] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

function contrast(a, b) {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

function setSwatchTones(root = document) {
  for (const swatch of root.querySelectorAll('.nudient-colour-swatch')) {
    const dot = swatch.querySelector('.nudient-colour-swatch__dot');
    if (!dot || !swatch.querySelector('.nudient-colour-swatch__mark')) continue;

    const style = getComputedStyle(swatch);
    const fill = readColour(getComputedStyle(dot).backgroundColor);
    const inverse = readColour(style.getPropertyValue('--color-primary-button-text').trim());
    const primary = readColour(style.getPropertyValue('--color-foreground-heading').trim());

    swatch.dataset.nudientTone = contrast(fill, primary) > contrast(fill, inverse) ? 'light' : 'dark';
  }
}

/* Rutan med färgnamnet: bara med mus, och inom färgraden */
const canHover = window.matchMedia('(hover: hover)');

function setHover() {
  if (canHover.matches) document.documentElement.dataset.nudientHover = '';
  else delete document.documentElement.dataset.nudientHover;
}

function placeTip(event) {
  const swatch = event.target.closest?.('.nudient-colour-swatch');
  const tip = swatch?.querySelector('.nudient-colour-swatch__tip');
  const row = swatch?.closest('.nudient-colour');
  if (!tip || !row) return;

  /* Direkt om rutan redan visas, annars i nästa bildruta */
  if (!shiftTip(tip, row)) requestAnimationFrame(() => shiftTip(tip, row));
}

function shiftTip(tip, row) {
  tip.style.removeProperty('--nudient-tip-shift');
  const tipBox = tip.getBoundingClientRect();
  const rowBox = row.getBoundingClientRect();
  if (!tipBox.width) return false;

  let shift = 0;
  if (tipBox.left < rowBox.left) shift = rowBox.left - tipBox.left;
  else if (tipBox.right > rowBox.right) shift = rowBox.right - tipBox.right;
  if (shift) tip.style.setProperty('--nudient-tip-shift', `${shift}px`);
  return true;
}

/* Sömlöst byte av färg och storlek, planen punkt 15.13 */
const SWAP_LINKS =
  'a.nudient-colour-swatch, a.nudient-size__chip, a.nudient-set-card, .nudient-model-row a.nudient-model-picker__option, a.nudient-model-row__magsafe';
const prefetched = new Set();
let swapClicked = false;

function prefetch(event) {
  const link = event.target.closest?.(SWAP_LINKS);
  if (!link || link.hasAttribute('aria-current') || prefetched.has(link.href)) return;
  /* Modellraden, M-086: adressen är inte klar förrän uppslagningen har svarat,
     och då kommer händelsen nudient:prefetch från assets/nudient-model-picker.js. */
  if (link.hasAttribute('data-nudient-pending')) return;
  prefetched.add(link.href);

  if (HTMLScriptElement.supports?.('speculationrules')) {
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [link.href] }] });
    document.head.append(rules);
  } else if (document.createElement('link').relList.supports('prefetch')) {
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = link.href;
    document.head.append(hint);
  }
}

/* Vid modellbyte hamnar kunden högst upp, tillägget till M-084 8 oktober
   2026. Scrollningen behålls bara vid färg- och storleksbyte. */
const MODEL_LINKS = '.nudient-model-row a.nudient-model-picker__option';

function keepPlace(event) {
  const link = event.target.closest?.(SWAP_LINKS);
  if (!link || event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (link.target && link.target !== '_self') return;

  const url = new URL(link.href);
  if (url.origin !== location.origin) return;

  /* Modellbytet: övergången utan scrollning, så att den nya sidan börjar
     högst upp, tillägget till M-084 8 oktober 2026. */
  if (link.matches(MODEL_LINKS)) {
    try {
      sessionStorage.setItem('nudient-keep-place', JSON.stringify({ path: url.pathname }));
    } catch {
      return;
    }
    optInTransition();
    return;
  }

  const anchor = link.closest('[data-nudient-keep-place]');
  if (!anchor?.id) return;

  /* Horizon scrollar .page-wrapper från 990, assets/scroll-container.js */
  const container = window.matchMedia('(min-width: 990px)').matches
    ? document.querySelector('.page-wrapper') ?? document.scrollingElement
    : document.scrollingElement;

  try {
    sessionStorage.setItem(
      'nudient-keep-place',
      JSON.stringify({
        path: url.pathname,
        anchor: anchor.id,
        top: anchor.getBoundingClientRect().top,
        scroll: container.scrollTop,
      })
    );
  } catch {
    return;
  }

  optInTransition();
}

/* Övergången för den här sidladdningen, utom vid minskad rörelse */
function optInTransition() {
  swapClicked = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!document.getElementById('nudient-view-transition')) {
    const optIn = document.createElement('style');
    optIn.id = 'nudient-view-transition';
    optIn.textContent = '@view-transition { navigation: auto; }';
    document.head.append(optIn);
  }
}

window.addEventListener('pageswap', (event) => {
  const transition = event.viewTransition;
  if (!transition || !document.getElementById('nudient-view-transition')) return;
  if (swapClicked) transition.types.add('page-navigation');
  else transition.skipTransition();
});

/* Tillbaka med bakåtknappen: sidan är som innan klicket */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  swapClicked = false;
  document.getElementById('nudient-view-transition')?.remove();
});

document.addEventListener('pointerover', prefetch);
document.addEventListener('nudient:prefetch', prefetch);
document.addEventListener('touchstart', prefetch, { passive: true });
document.addEventListener('click', keepPlace);

setHover();
canHover.addEventListener('change', setHover);
document.addEventListener('pointerover', placeTip);
document.addEventListener('focusin', placeTip);

formatNumbers();
setSwatchTones();

/* Temaredigeraren ritar om block utan att ladda om sidan */
document.addEventListener('shopify:block:select', (event) => {
  formatNumbers(event.target);
  setSwatchTones(event.target);
});
/* Färgprickar som ritas efter laddningen, merförsäljningen M-153 */
document.addEventListener('nudient:swatches', (event) => {
  if (event.target instanceof Element) setSwatchTones(event.target);
});

document.addEventListener('shopify:section:load', (event) => {
  formatNumbers(event.target);
  setSwatchTones(event.target);
});
